<template>
  <form class="ui-ai-conversation-form" @submit.prevent="handleSubmit">
    <UIInputText
      v-model="message"
      :disabled="loading"
      :class="inputClasses"
      placeholder="Ask anything"
      class="ui-ai-conversation-form__input"
      @ready="handleReady"
    />
    <UIButtonIcon
      :loading
      :disabled="isButtonDisabled"
      :leading="ArrowUpIcon"
    />
  </form>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useTimeoutFn } from '@vueuse/core'

import UIButtonIcon from '../Button/ButtonIcon.vue'
import UIInputText from '../Input/Text/Text.vue'
import { ArrowUpIcon } from '@heroicons/vue/24/outline'

type Props = {
  started?: boolean
  loading?: boolean
}

type Emits = {
  submit: [message: string, callback?: () => void]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  appendMessage: (value: string) => {
    const chunks = []
    message.value && chunks.push(message.value)
    chunks.push(value)
    message.value = chunks.join(', ')
    stop()
    attract.value = true
    inputEl.value?.focus()
    start()
  },
  reset: () => {
    message.value = ''
  },
})

const message = ref('')
const attract = ref(false)

const inputEl = ref<HTMLInputElement>()

const { stop, start } = useTimeoutFn(() => {
  attract.value = false
}, 700)

const inputClasses = computed(() => ({
  'ui-ai-conversation-form__input--attract': attract.value && message.value,
}))

const isButtonDisabled = computed(() => {
  return !message.value || props.loading
})

const handleSubmit = () => {
  emit('submit', message.value, () => {
    message.value = ''
  })
}

const handleReady = (el: HTMLInputElement) => {
  inputEl.value = el
  el.focus()
}
</script>

<style>
.ui-ai-conversation-form {
  @apply flex gap-2;
  @apply py-1;

  &__input {
    @apply flex-auto;
    @apply outline outline-transparent outline-offset-2;
    @apply duration-700;
    @apply rounded-full;
    transition-property: outline;

    &--attract {
      @apply outline-4 outline-indigo-200 dark:outline-indigo-800;
    }

    .ui-input-text__input {
      @apply px-5;
      @apply rounded-full;
    }
  }

  .ui-button {
    @apply rounded-full;
  }

  .ui-button__icon {
    @apply size-5;
  }
}
</style>
